import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, UsersIcon, HeartIcon, FileTextIcon, HomeIcon } from 'lucide-react';
import { CategorySection } from '../components/CategorySection';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Birth & Death Records
{
  id: 'bd1',
  title: 'Birth Certificate Request',
  description: 'Apply for new or replacement birth certificates',
  category: 'Birth & Death Records',
  estimatedTime: '3-5 business days',
  fee: '$25.00'
}, {
  id: 'bd2',
  title: 'Death Certificate Request',
  description: 'Request official death certificates',
  category: 'Birth & Death Records',
  estimatedTime: '3-5 business days',
  fee: '$25.00'
}, {
  id: 'bd3',
  title: 'Birth Registration',
  description: 'Register a newborn birth',
  category: 'Birth & Death Records',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'bd4',
  title: 'Death Registration',
  description: 'Register a death and obtain documentation',
  category: 'Birth & Death Records',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
},
// Marriage & Family
{
  id: 'mf1',
  title: 'Marriage License Application',
  description: 'Apply for a marriage license',
  category: 'Marriage & Family',
  estimatedTime: '5-7 business days',
  fee: '$75.00'
}, {
  id: 'mf2',
  title: 'Marriage Certificate Request',
  description: 'Request official marriage certificates',
  category: 'Marriage & Family',
  estimatedTime: '3-5 business days',
  fee: '$25.00'
}, {
  id: 'mf3',
  title: 'Civil Union Registration',
  description: 'Register a civil union partnership',
  category: 'Marriage & Family',
  estimatedTime: '5-7 business days',
  fee: '$50.00'
}, {
  id: 'mf4',
  title: 'Name Change Processing',
  description: 'Process legal name changes after marriage',
  category: 'Marriage & Family',
  estimatedTime: '10-15 business days',
  fee: '$100.00'
},
// Identity Documents
{
  id: 'id1',
  title: 'Municipal ID Card',
  description: 'Apply for official municipal identification card',
  category: 'Identity Documents',
  estimatedTime: '5-7 business days',
  fee: '$35.00'
}, {
  id: 'id2',
  title: 'Document Authentication',
  description: 'Authenticate official documents and certificates',
  category: 'Identity Documents',
  estimatedTime: '3-5 business days',
  fee: '$30.00 per document'
}, {
  id: 'id3',
  title: 'Identity Verification',
  description: 'Verify identity for official purposes',
  category: 'Identity Documents',
  estimatedTime: '2-3 business days',
  fee: '$25.00'
}, {
  id: 'id4',
  title: 'Certificate Apostille',
  description: 'Obtain apostille for international use',
  category: 'Identity Documents',
  estimatedTime: '7-10 business days',
  fee: '$45.00'
},
// Residency Services
{
  id: 'rs1',
  title: 'Residency Certificate',
  description: 'Apply for official proof of residency',
  category: 'Residency Services',
  estimatedTime: '3-5 business days',
  fee: '$40.00'
}, {
  id: 'rs2',
  title: 'Address Change Registration',
  description: 'Register change of address',
  category: 'Residency Services',
  estimatedTime: '2-3 business days',
  fee: '$15.00'
}, {
  id: 'rs3',
  title: 'Household Composition Certificate',
  description: 'Request official household member documentation',
  category: 'Residency Services',
  estimatedTime: '3-5 business days',
  fee: '$30.00'
}, {
  id: 'rs4',
  title: 'Historical Residency Verification',
  description: 'Obtain proof of historical residency',
  category: 'Residency Services',
  estimatedTime: '5-7 business days',
  fee: '$35.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Birth & Death Records':
      return <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-3580" />;
    case 'Marriage & Family':
      return <HeartIcon className="h-6 w-6 text-pink-600" data-id="element-3581" />;
    case 'Identity Documents':
      return <UsersIcon className="h-6 w-6 text-purple-600" data-id="element-3582" />;
    case 'Residency Services':
      return <HomeIcon className="h-6 w-6 text-green-600" data-id="element-3583" />;
    default:
      return <UsersIcon className="h-6 w-6 text-gray-600" data-id="element-3584" />;
  }
};
export const CivilRegistryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3585">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3586">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3587">
          <a href="/" className="hover:text-gray-700" data-id="element-3588">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3589" />
          <span className="text-gray-900" data-id="element-3590">Civil Registry Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3591">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3592">
            Civil Registry Services
          </h1>
          <p className="text-gray-600" data-id="element-3593">
            Manage vital records, identity documents, and residency services
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3594">
          <div className="max-w-md relative" data-id="element-3595">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3596">
              <SearchIcon className="h-5 w-5" data-id="element-3597" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3598" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} icon={<CategoryIcon category={category} data-id="element-3600" />} buttonText="Request Document" data-id="element-3599" />)}
      </div>
    </div>;
};